import React, { useState, useEffect } from 'react';
import './MaintenanceDateTimeInput.css';

const CT_ZONE = 'America/Chicago';

/**
 * Split ISO string into date and 12-hour time parts in Central Time (so admin sees what they entered).
 */
function isoToParts(isoString) {
  if (!isoString) return { date: '', hour: '', minute: '', ampm: 'AM' };
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return { date: '', hour: '', minute: '', ampm: 'AM' };
    const opts = { timeZone: CT_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true };
    const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const date = `${get('year')}-${get('month').padStart(2, '0')}-${get('day').padStart(2, '0')}`;
    const hour = get('hour');
    const minute = get('minute').padStart(2, '0');
    const ampm = (get('dayPeriod') || 'AM').toUpperCase();
    return { date, hour, minute, ampm };
  } catch {
    return { date: '', hour: '', minute: '', ampm: 'AM' };
  }
}

/**
 * Returns UTC offset in hours to add to Central Time to get UTC.
 * America/Chicago: CST = UTC-6 (add 6), CDT = UTC-5 (add 5).
 * US DST: 2nd Sunday March through 1st Sunday November.
 */
function getCTOffsetHours(year, monthZero, day) {
  const march1 = new Date(year, 2, 1);
  const nov1 = new Date(year, 10, 1);
  const secondSundayMarch = 8 + (7 - march1.getDay()) % 7;
  const firstSundayNov = 1 + (7 - nov1.getDay()) % 7;
  const inDST = (monthZero > 2 || (monthZero === 2 && day >= secondSundayMarch)) &&
    (monthZero < 10 || (monthZero === 10 && day < firstSundayNov));
  return inDST ? 5 : 6; // CDT = +5, CST = +6
}

/**
 * Combine date + hour (1-12) + minute + ampm as Central Time and return ISO string (UTC).
 * Admin enters time in CT; we convert to UTC for storage so users see correct local time.
 */
function partsToIso(date, hour, minute, ampm) {
  if (!date || hour === '' || minute === '') return null;
  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 1 || h > 12 || m < 0 || m > 59) return null;
  const hour24 = ampm === 'AM' ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12);
  const [y, mo, day] = date.split('-').map(Number);
  const monthZero = mo - 1;
  const offsetHours = getCTOffsetHours(y, monthZero, day);
  // 4:30 PM CT = 16:30 CT → UTC is 16:30 + offsetHours (22:30 UTC for CST)
  const utcMs = Date.UTC(y, monthZero, day, hour24 + offsetHours, m);
  const d = new Date(utcMs);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Date + typeable time (hour, minute, AM/PM) with Apply to save. No auto-save.
 */
const MaintenanceDateTimeInput = ({ value, onChange, label }) => {
  const initial = isoToParts(value);
  const [date, setDate] = useState(initial.date);
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [ampm, setAmpm] = useState(initial.ampm);

  useEffect(() => {
    const next = isoToParts(value);
    setDate(next.date);
    setHour(next.hour);
    setMinute(next.minute);
    setAmpm(next.ampm);
  }, [value]);

  const handleHourChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    setHour(v);
  };

  const handleMinuteChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMinute(v);
  };

  const handleApply = () => {
    const iso = partsToIso(date, hour, minute, ampm);
    if (iso) onChange(iso);
  };

  const handleClear = () => {
    setDate('');
    setHour('');
    setMinute('');
    setAmpm('AM');
    onChange(null);
  };

  const canApply = date && hour !== '' && minute !== '' && partsToIso(date, hour, minute, ampm) != null;
  const hasValue = !!value;

  return (
    <div className="maintenance-datetime">
      {label && (
        <span className="maintenance-datetime__label">{label}</span>
      )}
      <div className="maintenance-datetime__row maintenance-datetime__row--fields">
        <input
          type="date"
          className="maintenance-datetime__input maintenance-datetime__input--date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Date"
        />
        <div className="maintenance-datetime__time-group">
          <input
            type="text"
            inputMode="numeric"
            className="maintenance-datetime__input maintenance-datetime__input--hour"
            value={hour}
            onChange={handleHourChange}
            placeholder="hr"
            aria-label="Hour (1-12)"
            maxLength={2}
          />
          <span className="maintenance-datetime__separator">:</span>
          <input
            type="text"
            inputMode="numeric"
            className="maintenance-datetime__input maintenance-datetime__input--minute"
            value={minute}
            onChange={handleMinuteChange}
            placeholder="min"
            aria-label="Minute (00-59)"
            maxLength={2}
          />
          <select
            className="maintenance-datetime__select maintenance-datetime__select--ampm"
            value={ampm}
            onChange={(e) => setAmpm(e.target.value)}
            aria-label="AM or PM"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
        <div className="maintenance-datetime__actions">
          <button
            type="button"
            className="maintenance-datetime__apply"
            onClick={handleApply}
            disabled={!canApply}
            aria-label="Apply date and time"
          >
            Apply
          </button>
          {hasValue && (
            <button
              type="button"
              className="maintenance-datetime__clear"
              onClick={handleClear}
              aria-label="Clear date and time"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDateTimeInput;
